/**
 * 将 MJML 转换为 HTML
 * 使用 mjml2html 和 PluginManager.renderWithData
 */

import mjml2html from "mjml";
import { PluginManager } from "../../src/utils/plugins/PluginManager";
import type { ConvertToHtmlInput, ConvertToHtmlOutput } from "../types";

export interface ConvertToHtmlOptions {
  /** 是否压缩输出 */
  minify?: boolean;
  /** 验证级别 */
  validationLevel?: "strict" | "soft" | "skip";
  /** 模板变量数据（用于 PluginManager.renderWithData） */
  mergetagsData?: Record<string, any>;
}

/**
 * 主函数：将 MJML 转换为 HTML
 * 支持使用 PluginManager.renderWithData 渲染模板变量
 */
export function convertToHtml(
  input: ConvertToHtmlInput,
  options: ConvertToHtmlOptions = {}
): ConvertToHtmlOutput {
  try {
    const { mjml } = input;
    const { minify = false, validationLevel = "soft", mergetagsData } = options;

    if (!mjml || typeof mjml !== "string") {
      throw new Error("Invalid input: mjml must be a non-empty string");
    }

    // 使用 mjml2html 转换
    const result = mjml2html(mjml, {
      validationLevel,
      minify,
    });

    if (result.errors && result.errors.length > 0) {
      // 报告验证错误
      console.warn("MJML validation errors:", result.errors);
    }

    let html = result.html;

    // 如果提供了模板变量数据，使用 PluginManager.renderWithData 渲染
    if (mergetagsData && Object.keys(mergetagsData).length > 0) {
      html = PluginManager.renderWithData(html, mergetagsData);
    }

    return {
      success: true,
      html,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const CONVERT_TO_HTML_TOOL = {
  name: "convert_to_html",
  description: `将 MJML 字符串转换为可发送的 HTML 邮件。
支持使用 PluginManager.renderWithData 渲染模板变量。

输入参数:
- mjml: MJML 格式的字符串（必填）
- minify: 是否压缩输出（默认 false）
- validationLevel: 验证级别 "strict" | "soft" | "skip"（默认 "soft"）
- mergetagsData: 模板变量数据（可选，用于渲染模板变量）

输出:
- success: boolean - 是否成功
- html: 转换后的 HTML 字符串（如果成功）
- error: 错误信息（如果失败）`,
  inputSchema: {
    type: "object" as const,
    properties: {
      mjml: {
        type: "string",
        description: "MJML 格式的字符串",
      },
      minify: {
        type: "boolean",
        description: "是否压缩输出",
      },
      validationLevel: {
        type: "string",
        enum: ["strict", "soft", "skip"],
        description: "MJML 验证级别",
      },
      mergetagsData: {
        type: "object",
        description: "模板变量数据，用于渲染模板变量",
      },
    },
    required: ["mjml"],
  },
};
